import { QueueingSystemParams, QueueingMetrics, PoolingComparison, SensitivityAnalysis, SimulationResult } from '../types';

export class QueueingCalculator {
  static calculateMetrics(params: QueueingSystemParams): QueueingMetrics {
    const { arrivalRate, serviceRate, arrivalCV, serviceCV, servers } = params;

    if (arrivalRate <= 0 || serviceRate <= 0 || servers <= 0) {
      return {
        utilization: 0,
        processTime: 0,
        queueTime: 0,
        cycleTime: 0,
        wip: 0,
        wipQueue: 0,
        throughput: 0,
        error: "Rates and servers must be positive."
      };
    }

    // Convert to per-minute rates for calculations
    const arrivalRateMin = arrivalRate / 60;
    const serviceRateMin = serviceRate / 60;
    const processTime = 1 / serviceRateMin;
    const utilization = arrivalRateMin / (servers * serviceRateMin);

    if (utilization >= 1) {
      return {
        utilization,
        processTime,
        queueTime: Infinity,
        cycleTime: Infinity,
        wip: Infinity,
        wipQueue: Infinity,
        throughput: arrivalRateMin,
        error: `Utilization (${(utilization * 100).toFixed(2)}%) is >= 100%. System is unstable.`
      };
    }

    // VUT equation implementation
    const variabilityTerm = (Math.pow(arrivalCV, 2) + Math.pow(serviceCV, 2)) / 2;
    
    let utilizationTerm: number;
    if (servers === 1) {
      // Single server: M/G/1 approximation
      utilizationTerm = utilization / (1 - utilization);
    } else {
      // Multi-server: G/G/m approximation
      const exponent = Math.sqrt(2 * (servers + 1)) - 1;
      utilizationTerm = Math.pow(utilization, exponent) / (servers * (1 - utilization));
    }

    const queueTime = variabilityTerm * utilizationTerm * processTime;
    const cycleTime = queueTime + processTime;
    const wip = arrivalRateMin * cycleTime;
    const wipQueue = arrivalRateMin * queueTime;

    return {
      utilization,
      processTime,
      queueTime,
      cycleTime,
      wip,
      wipQueue,
      throughput: arrivalRateMin,
      error: undefined
    };
  }

  static generateUtilizationCurve(baseParams: QueueingSystemParams): Array<{x: number, y: number}> {
    const { serviceRate, arrivalCV, serviceCV, servers } = baseParams;
    const data: Array<{x: number, y: number}> = [];

    // Calculate current utilization to determine starting point
    const currentMetrics = this.calculateMetrics(baseParams);
    const startUtilization = currentMetrics.utilization < 0.5 ? 10 : 50;

    // Start at appropriate utilization and go to 99% for better visualization
    for (let i = startUtilization; i <= 99; i++) {
      const utilizationPoint = i / 100;
      const arrivalRatePoint = utilizationPoint * servers * serviceRate;
      
      const metrics = this.calculateMetrics({
        ...baseParams,
        arrivalRate: arrivalRatePoint
      });

      if (metrics.queueTime !== Infinity && !metrics.error) {
        data.push({ x: utilizationPoint, y: metrics.queueTime });
      }
    }

    return data;
  }

  static generateVariabilityCurves(baseParams: QueueingSystemParams): Record<string, Array<{x: number, y: number}>> {
    const scenarios = [
      { label: 'Low Variability (CV=0.25)', arrivalCV: 0.25, serviceCV: 0.25 },
      { label: 'M/M/1 (CV=1)', arrivalCV: 1, serviceCV: 1 },
      { label: 'Current Parameters', arrivalCV: baseParams.arrivalCV, serviceCV: baseParams.serviceCV },
      { label: 'High Variability (CV=2)', arrivalCV: 2, serviceCV: 2 },
    ];

    const curves: Record<string, Array<{x: number, y: number}>> = {};

    // Calculate current utilization to determine starting point
    const currentMetrics = this.calculateMetrics(baseParams);
    const startUtilization = currentMetrics.utilization < 0.5 ? 10 : 50;

    scenarios.forEach(scenario => {
      const data: Array<{x: number, y: number}> = [];
      
      // Start at appropriate utilization and go to 99% for better visualization
      for (let i = startUtilization; i <= 99; i++) {
        const utilizationPoint = i / 100;
        const arrivalRatePoint = utilizationPoint * baseParams.servers * baseParams.serviceRate;
        
        const metrics = this.calculateMetrics({
          ...baseParams,
          arrivalRate: arrivalRatePoint,
          arrivalCV: scenario.arrivalCV,
          serviceCV: scenario.serviceCV
        });

        if (metrics.queueTime !== Infinity && !metrics.error) {
          data.push({ x: utilizationPoint, y: metrics.queueTime });
        }
      }
      
      curves[scenario.label] = data;
    });

    return curves;
  }

  static calculatePoolingComparison(params: QueueingSystemParams): PoolingComparison {
    const { arrivalRate, serviceRate, arrivalCV, serviceCV, servers } = params;

    // Dedicated queues: each server has arrival_rate/servers
    const dedicatedParams = {
      ...params,
      arrivalRate: arrivalRate / servers,
      servers: 1
    };
    const dedicatedMetrics = this.calculateMetrics(dedicatedParams);

    // Pooled queue: single queue with all servers
    const pooledMetrics = this.calculateMetrics(params);

    return {
      dedicated: {
        ...dedicatedMetrics,
        totalWIP: dedicatedMetrics.wip * servers,
        totalWIPQueue: dedicatedMetrics.wipQueue * servers
      },
      pooled: pooledMetrics
    };
  }

  static performSensitivityAnalysis(
    baseParams: QueueingSystemParams,
    parameter: keyof QueueingSystemParams,
    range: { min: number; max: number; steps: number }
  ): SensitivityAnalysis {
    const { min, max, steps } = range;
    const step = (max - min) / (steps - 1);
    
    const values: number[] = [];
    const queueTimes: number[] = [];
    const cycleTimes: number[] = [];
    const utilizations: number[] = [];

    for (let i = 0; i < steps; i++) {
      const value = min + (step * i);
      values.push(value);

      const testParams = { ...baseParams, [parameter]: value };
      const metrics = this.calculateMetrics(testParams);

      queueTimes.push(metrics.queueTime === Infinity ? 0 : metrics.queueTime);
      cycleTimes.push(metrics.cycleTime === Infinity ? 0 : metrics.cycleTime);
      utilizations.push(metrics.utilization);
    }

    return {
      parameter: parameter as string,
      baseValue: baseParams[parameter] as number,
      values,
      queueTimes,
      cycleTimes,
      utilizations
    };
  }

  static runSimulation(
    params: QueueingSystemParams,
    duration: number = 1000,
    warmup: number = 100
  ): SimulationResult[] {
    const { arrivalRate, serviceRate, servers, arrivalCV, serviceCV } = params;
    const results: SimulationResult[] = [];
    
    // Discrete event simulation with proper multi-server handling
    let currentTime = 0;
    let queueLength = 0;
    let nextArrival = this.generateInterarrivalTime(arrivalRate / 60, arrivalCV);
    
    // Track individual server departure times - initialize all as idle (Infinity)
    let serverDepartures: number[] = new Array(servers).fill(Infinity);
    
    let totalWaitTime = 0;
    let totalCustomers = 0;
    let customersServed = 0;

    while (currentTime < duration + warmup) {
      // Find the next event (arrival or earliest departure)
      const nextDeparture = Math.min(...serverDepartures);
      const nextEvent = Math.min(nextArrival, nextDeparture);
      
      if (nextEvent === nextArrival) {
        // Process arrival
        currentTime = nextArrival;
        
        // Find an idle server (departure time = Infinity)
        const idleServerIndex = serverDepartures.findIndex(dep => dep === Infinity);
        
        if (idleServerIndex !== -1) {
          // Server available - start service immediately
          serverDepartures[idleServerIndex] = currentTime + this.generateServiceTime(serviceRate / 60, serviceCV);
        } else {
          // All servers busy - join queue
          queueLength++;
        }
        
        // Schedule next arrival
        nextArrival = currentTime + this.generateInterarrivalTime(arrivalRate / 60, arrivalCV);
        
      } else {
        // Process departure from earliest server
        currentTime = nextDeparture;
        const departingServerIndex = serverDepartures.findIndex(dep => dep === nextDeparture);
        
        if (queueLength > 0) {
          // Customer waiting in queue - start serving immediately
          queueLength--;
          serverDepartures[departingServerIndex] = currentTime + this.generateServiceTime(serviceRate / 60, serviceCV);
        } else {
          // No queue - server becomes idle
          serverDepartures[departingServerIndex] = Infinity;
        }
        
        customersServed++;
      }

      // Record data after warmup period (sample every few events for performance)
      if (currentTime >= warmup && customersServed % 10 === 0) {
        const busyServers = serverDepartures.filter(dep => dep !== Infinity).length;
        results.push({
          time: currentTime - warmup,
          queueLength,
          systemLength: queueLength + busyServers,
          waitTime: customersServed > 0 ? totalWaitTime / customersServed : 0,
          serviceTime: 1 / (serviceRate / 60)
        });
      }
    }

    return results;
  }

  private static generateExponential(rate: number): number {
    return -Math.log(1 - Math.random()) / rate;
  }

  private static generateInterarrivalTime(rate: number, cv: number): number {
    if (cv === 1) {
      // Exponential distribution (M)
      return this.generateExponential(rate);
    } else if (cv === 0) {
      // Deterministic (D)
      return 1 / rate;
    } else {
      // Use Gamma distribution to approximate general distribution with given CV
      const mean = 1 / rate;
      const variance = Math.pow(mean * cv, 2);
      const shape = Math.pow(mean, 2) / variance; // k parameter
      const scale = variance / mean; // θ parameter
      return this.generateGamma(shape, scale);
    }
  }

  private static generateServiceTime(rate: number, cv: number): number {
    if (cv === 1) {
      // Exponential distribution (M)
      return this.generateExponential(rate);
    } else if (cv === 0) {
      // Deterministic (D)
      return 1 / rate;
    } else {
      // Use Gamma distribution to approximate general distribution with given CV
      const mean = 1 / rate;
      const variance = Math.pow(mean * cv, 2);
      const shape = Math.pow(mean, 2) / variance; // k parameter
      const scale = variance / mean; // θ parameter
      return this.generateGamma(shape, scale);
    }
  }

  private static generateGamma(shape: number, scale: number): number {
    // Simple acceptance-rejection method for Gamma distribution
    if (shape < 1) {
      // Use Ahrens-Dieter method for shape < 1
      return this.generateGamma(shape + 1, scale) * Math.pow(Math.random(), 1 / shape);
    }
    
    // Use Marsaglia and Tsang method for shape >= 1
    const d = shape - 1/3;
    const c = 1 / Math.sqrt(9 * d);
    
    while (true) {
      let x, v;
      do {
        x = this.generateNormal(0, 1);
        v = 1 + c * x;
      } while (v <= 0);
      
      v = v * v * v;
      const u = Math.random();
      
      if (u < 1 - 0.0331 * x * x * x * x) {
        return d * v * scale;
      }
      
      if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) {
        return d * v * scale;
      }
    }
  }

  private static generateNormal(mean: number, stdDev: number): number {
    // Box-Muller transform
    if (this.spare !== undefined) {
      const tmp = this.spare;
      this.spare = undefined;
      return tmp * stdDev + mean;
    }
    
    const u = Math.random();
    const v = Math.random();
    const mag = stdDev * Math.sqrt(-2 * Math.log(u));
    this.spare = mag * Math.cos(2 * Math.PI * v);
    return mag * Math.sin(2 * Math.PI * v) + mean;
  }

  private static spare: number | undefined;

  static calculateCVFromMeanStd(mean: number, stdDev: number): number {
    return mean > 0 ? stdDev / mean : 0;
  }

  static calculateStdFromMeanCV(mean: number, cv: number): number {
    return mean * cv;
  }

  static formatNumber(num: number, decimals: number = 2): string {
    if (!isFinite(num)) return '∞';
    return num.toFixed(decimals);
  }

  static formatPercentage(num: number, decimals: number = 1): string {
    if (!isFinite(num)) return '∞%';
    return `${(num * 100).toFixed(decimals)}%`;
  }
}